/** Shapes returned by the PHP API. Kept in step with api/src/Repository by hand. */

export type MemberStatus =
  | 'prospective'
  | 'active'
  | 'lapsed'
  | 'resigned'
  | 'deceased';

/** The three a human sets. active/lapsed are derived from subscriptions. */
export const HUMAN_SET_STATUSES: MemberStatus[] = ['prospective', 'resigned', 'deceased'];

export const ALL_STATUSES: MemberStatus[] = [
  'prospective',
  'active',
  'lapsed',
  'resigned',
  'deceased',
];

export interface Identity {
  member: {
    id: number;
    first_name: string;
    last_name: string;
    email: string | null;
  };
  permissions: string[];
  csrf_token: string;
  /** Flags that shape the screens. Not permissions, and not secrets. */
  settings: { gift_aid: boolean };
}

export interface AttentionItem {
  kind: string;
  severity: 'warn' | 'info';
  title: string;
  detail: string;
  /** null when there is nowhere useful to go — a server setting, say. */
  link: string | null;
  link_label: string | null;
}

export interface DashboardData {
  year: { id: number; year: number; opens_on: string; due_on: string } | null;
  attention: AttentionItem[];
  /** null for anybody without subscriptions_view — the server omits it. */
  figures: { members: number; settled: number; outstanding: number } | null;
  gift_aid: boolean;
}

export interface NewsletterImage {
  id: number;
  filename: string;
  original_name: string | null;
  mime: string;
  width: number;
  height: number;
  bytes: number;
  size: string;
  url: string;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

export interface NewsletterImageList {
  images: NewsletterImage[];
  /** Which one heads every newsletter, if any has been chosen. */
  logo_image_id: number | null;
  /** The server's limit, so a picture over it is refused before it is sent. */
  max_bytes: number;
  /** How wide the header picture is drawn in the email, in pixels. */
  logo_display_width: number;
}

/**
 * The block types the composer can add, plus the two it cannot.
 *
 * The server refuses anything not on this list, so the two have to agree —
 * but the labels and the order below are the screen's business, not the
 * server's, which is why they live here.
 */
export type NewsletterBlockType =
  | 'header'
  | 'heading'
  | 'text'
  | 'image'
  | 'image_text'
  | 'columns'
  | 'button'
  | 'dates'
  | 'divider'
  | 'footer';

export interface NewsletterDate {
  when: string;
  what: string;
  where: string;
}

/** Whatever the block type needs. Every field is optional; the server cleans. */
export interface NewsletterBlockContent {
  issue?: string;
  text?: string;
  image_id?: number;
  url?: string;
  alt?: string;
  caption?: string;
  left_heading?: string;
  left_text?: string;
  right_heading?: string;
  right_text?: string;
  label?: string;
  items?: NewsletterDate[];
}

export interface NewsletterBlock {
  /** Null for a block added on the screen and not yet saved. */
  id: number | null;
  type: NewsletterBlockType;
  content: NewsletterBlockContent;
}

export interface NewsletterSummary {
  id: number;
  title: string;
  subject: string;
  status: 'draft' | 'sent';
  public_token: string;
  campaign_id: number | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  created_by_name: string | null;
  block_count: number;
  sent_count: number;
}

export interface Newsletter extends Omit<NewsletterSummary, 'block_count' | 'sent_count'> {
  blocks: NewsletterBlock[];
}

/** A campaign preview, plus what the composer wants to say about the body. */
export interface NewsletterAudience extends CampaignPreview {
  warnings: string[];
}

export interface NewsletterSendResult {
  campaign_id: number;
  queued: number;
  unreachable: number;
  newsletter: Newsletter;
}

export interface NewsletterPreview {
  html: string;
  text: string;
  /** Things worth saying before this goes to a hundred and fifty people. */
  warnings: string[];
}

export interface MemberImportColumns {
  headers: string[];
  /** Column index → field name. The server's guess, yours to correct. */
  mapping: Record<string, string>;
  /** Which columns it recognised outright, so the rest can be pointed at. */
  confident: number[];
  /** Field name → the label to show for it. */
  fields: Record<string, string>;
  row_count: number;
  truncated: boolean;
  sample: string[][];
}

export interface ImportedPerson {
  title: string;
  first_name: string;
  last_name: string;
  address_1: string;
  address_2: string;
  town: string;
  postcode: string;
  telephone: string;
  mobile: string;
  email: string;
  bbka_number: string;
  joined_date: string;
  date_of_birth: string;
  status: string;
}

export interface MemberImportRow {
  /** The row number as the spreadsheet shows it, headings counted. */
  line: number;
  state: 'ready' | 'warning' | 'blocked';
  notes: string[];
  member: ImportedPerson;
}

export interface MemberImportPreview {
  rows: MemberImportRow[];
  summary: { total: number; ready: number; warning: number; blocked: number };
  truncated: boolean;
}

export interface Setting {
  key: string;
  label: string;
  description: string;
  type: 'bool' | 'int' | 'text';
  value: boolean | number | string;
}

export interface MemberListRow {
  id: number;
  title: string | null;
  first_name: string;
  last_name: string;
  town: string | null;
  postcode: string | null;
  email: string | null;
  telephone: string | null;
  mobile: string | null;
  status: MemberStatus;
  joined_date: string | null;
  is_mentor: boolean;
  bbka_number: string | null;
  has_login: boolean;
  last_login_at: string | null;
}

export interface MemberList {
  rows: MemberListRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface Member extends MemberListRow {
  address_1: string | null;
  address_2: string | null;
  date_of_birth: string | null;
  partner_of_member_id: number | null;
  partner_of_name: string | null;
  guardian_name: string | null;
  guardian_email: string | null;
  mentor_capacity: number;
  gift_aid: boolean | null;
  gift_aid_declared_on: string | null;
  created_at: string;
  updated_at: string;
  permissions: string[];
}

/** Exactly the fields api/src/Repository/MemberRepository::EDITABLE_FIELDS allows. */
export interface MemberEdit {
  title: string | null;
  first_name: string;
  last_name: string;
  address_1: string | null;
  address_2: string | null;
  town: string | null;
  postcode: string | null;
  telephone: string | null;
  mobile: string | null;
  email: string | null;
  date_of_birth: string | null;
  guardian_name: string | null;
  guardian_email: string | null;
  bbka_number: string | null;
  joined_date: string | null;
  is_mentor: boolean;
  mentor_capacity: number;
  /** null means never asked, which is not the same answer as no. */
  gift_aid: boolean | null;
}

export interface PermissionDefinition {
  id: number;
  code: string;
  name: string;
  description: string | null;
}

export interface MembershipCategory {
  id: number;
  code: string;
  name: string;
  requires_parent: boolean;
  max_age: number | null;
  carries_bdi: boolean;
  is_active: boolean;
}

export interface DonationFund {
  id: number;
  name: string;
  is_active: boolean;
}

export interface CategoryRate {
  id: number;
  membership_category_id: number;
  amount: number;
  category_code: string;
  category_name: string;
  category_is_active: boolean;
}

export interface BdiBand {
  id: number;
  max_colonies: number;
  premium: number;
  sort_order: number;
}

export interface MembershipYearSummary {
  id: number;
  year: number;
  opens_on: string;
  due_on: string;
  bdi_cutoff_date: string;
  bdi_delay_days: number;
  is_current: boolean;
}

export interface MembershipYear extends MembershipYearSummary {
  category_rates: CategoryRate[];
  bdi_bands: BdiBand[];
  /** Whether opens_on is still in the future — the boundary the API enforces itself. */
  can_delete_rates: boolean;
}

export interface JoinCategory {
  id: number;
  code: string;
  name: string;
  requires_parent: boolean;
  max_age: number | null;
  carries_bdi: boolean;
  amount: number;
}

export interface JoinBdiBand {
  max_colonies: number;
  premium: number;
}

export interface JoinDonationFund {
  id: number;
  name: string;
}

export interface JoinPricingClosed {
  open: false;
}

export interface JoinPricingOpen {
  open: true;
  /** Whether the association claims Gift Aid — set by the committee. */
  gift_aid: boolean;
  membership_year_id: number;
  year: number;
  due_on: string;
  bdi_cutoff_date: string;
  categories: JoinCategory[];
  bdi_bands: JoinBdiBand[];
  donation_funds: JoinDonationFund[];
}

export type JoinPricing = JoinPricingOpen | JoinPricingClosed;

export interface JoinResult {
  member: { id: number; first_name: string; last_name: string; has_login: boolean };
  subscription: {
    id: number;
    year: number;
    category_name: string;
    category_amount: number;
    colony_count: number | null;
    bdi_amount: number;
    needs_bdi_referral: boolean;
    donations_total: number;
    total_due: number;
    due_on: string | null;
  };
}

export type SubscriptionStatus =
  | 'awaiting_payment'
  | 'part_paid'
  | 'paid'
  | 'waived'
  | 'cancelled';

/** The two a human sets. paid / part_paid / awaiting_payment follow the money. */
export const MANUAL_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = ['waived', 'cancelled'];

export interface Payment {
  id: number;
  amount: number;
  method: 'bacs' | 'cash' | 'cheque' | 'card';
  reference: string | null;
  received_on: string;
  recorded_at: string;
  recorded_by_name: string | null;
}

export interface SubscriptionRow {
  id: number;
  member_id: number;
  title: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  category_name: string;
  status: SubscriptionStatus;
  total_due: number;
  paid_total: number;
  outstanding: number;
  colony_count: number | null;
  needs_bdi_referral: boolean;
  bdi_cover_starts_on: string | null;
  applied_at: string;
}

export interface Subscription extends SubscriptionRow {
  payments: Payment[];
  year: number;
  carries_bdi: boolean;
  bdi_amount: number;
  category_amount: number;
}

export interface SubscriptionSummary {
  subscriptions: number;
  total_due: number;
  total_received: number;
  paid_count: number;
  part_paid_count: number;
  awaiting_count: number;
  waived_count: number;
  bdi_referral_count: number;
}

export interface MemberSubscription {
  id: number;
  year: number;
  category_name: string;
  status: SubscriptionStatus;
  total_due: number;
  paid_total: number;
  outstanding: number;
  needs_bdi_referral: boolean;
  bdi_cover_starts_on: string | null;
}

export interface ImportMatch {
  subscription_id: number;
  member_id: number;
  name: string;
  outstanding: number;
  total_due: number;
  confidence: number;
  reason: string;
}

export interface ImportRow {
  date: string;
  amount: number;
  reference: string;
  fingerprint: string;
  line: number;
  already_imported: boolean;
  matches: ImportMatch[];
}

export interface ImportPreview {
  rows: ImportRow[];
  summary: {
    rows: number;
    matched: number;
    confident: number;
    already_imported: number;
  };
  confident_at: number;
  /** The file had no usable headings, so the columns were worked out from the data. */
  columns_inferred: boolean;
}

export interface EmailTemplate {
  id: number;
  code: string;
  name: string;
  description: string | null;
  subject: string;
  body_html: string;
  is_system: boolean;
  updated_at?: string;
}

export interface AudienceFilter {
  status?: string;
  category_code?: string;
  payment?: '' | 'settled' | 'owing' | 'not_renewed';
  is_mentor?: string;
}

export interface CampaignPreview {
  recipient_count: number;
  /** Merge fields that would come out blank, and for how many recipients. */
  empty_fields: Record<string, number>;
  unreachable_count: number;
  unreachable: { member_id: number; name: string }[];
  via_guardian_count: number;
  sample: { name: string; email: string; subject: string; body_html: string } | null;
}

export interface EmailCampaign {
  id: number;
  subject: string;
  created_at: string;
  sent_at: string | null;
  created_by_name: string | null;
  recipients: number;
  sent: number;
  failed: number;
  audience_filter: AudienceFilter | null;
}

export interface EmailDelivery {
  id: number;
  campaign_id: number | null;
  template_code: string | null;
  member_id: number;
  member_name: string;
  email: string;
  subject: string;
  body_html?: string;
  status: 'queued' | 'sent' | 'bounced' | 'failed';
  error: string | null;
  queued_at: string;
  sent_at: string | null;
}

export interface ScheduledReminder {
  id: number;
  name: string;
  template_code: string;
  subject: string | null;
  relative_to: 'year_opens' | 'due_on' | 'bdi_cutoff';
  offset_days: number;
  audience_payment: string;
  is_active: boolean;
  trigger_on: string;
  window_closes_on: string;
  state: 'off' | 'upcoming' | 'due' | 'passed';
  due_now: boolean;
  would_reach: number;
}

export interface ReminderSchedule {
  year: { id: number; year: number; opens_on: string; due_on: string; bdi_cutoff_date: string } | null;
  reminders: ScheduledReminder[];
  catch_up_days: number;
}

export type MentorshipStatus = 'proposed' | 'active' | 'completed' | 'declined';

export interface Mentorship {
  id: number;
  status: MentorshipStatus;
  mentor_member_id: number;
  mentee_member_id: number;
  mentor_name: string;
  mentee_name: string;
  mentor_town?: string | null;
  mentee_town?: string | null;
  matched_by_name: string | null;
  started_on: string | null;
  ended_on: string | null;
  notes: string | null;
  introductions_queued?: number;
}

export interface MentorSuggestion {
  member_id: number;
  name: string;
  town: string | null;
  postcode: string | null;
  mentor_capacity: number;
  current_mentees: number;
  has_room: boolean;
  miles: number | null;
  reason: string;
}

export interface AwaitingMentor {
  id: number;
  first_name: string;
  last_name: string;
  town: string | null;
  postcode: string | null;
  course_name: string;
  course_year: number;
  completed_on: string | null;
}

export interface CourseSummary {
  id: number;
  name: string;
  year: number;
  attendee_count: number;
  completed_count: number;
}

export interface Apiary {
  id: number;
  member_id: number;
  label: string;
  postcode: string;
  colony_count: number;
  /** Whether the postcode resolves against postcode_point. If not, no radius search will find it. */
  is_placed: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ApiaryList {
  apiaries: Apiary[];
  /**
   * What they declared on this year's subscription, which set their BDI band.
   * Allowed to differ from the apiary totals — that figure is frozen.
   */
  declared_colonies: number | null;
}

export interface NearbyApiary {
  id: number;
  label: string;
  postcode: string;
  colony_count: number;
  member_id: number;
  first_name: string;
  last_name: string;
  status: MemberStatus;
  telephone: string | null;
  mobile: string | null;
  email: string | null;
  metres: number;
  miles: number;
}

export type UnplacedApiary = Omit<NearbyApiary, 'metres' | 'miles' | 'status'>;

export interface ApiarySearch {
  origin: { postcode: string; easting: number; northing: number } | null;
  within: NearbyApiary[];
  /** Apiaries whose postcode resolves to nothing — neither in nor out of the radius. */
  unplaced: UnplacedApiary[];
  radius_metres: number;
}

export interface Er2Warning {
  kind: string;
  detail: string;
  members: string[];
}

export interface Er2Preview {
  year: number;
  columns: string[];
  /** One entry per column name, so the preview table cannot drift from the file. */
  rows: Record<string, string>[];
  warnings: Er2Warning[];
  /** The columns that are decisions rather than facts, shown before the download. */
  assumptions: string[];
  counts: { rows: number; colonies: number; premium_pence: number };
}

export interface AgmReport {
  year: {
    id: number;
    year: number;
    opens_on: string;
    due_on: string;
    bdi_cutoff_date: string;
    is_current: boolean;
  };
  membership: {
    members: number;
    waived: number;
    new: number;
    renewed: number;
    lapsed: number;
    /** null when there is no previous year to compare against. */
    retention_percent: number | null;
    has_previous_year: boolean;
    by_category: { category: string; members: number; subscriptions: number; bdi: number }[];
  };
  money: {
    subscriptions: number;
    bdi: number;
    donations: number;
    donations_by_fund: { fund: string; amount: number; gifts: number }[];
    billed: number;
    waived: number;
    received: number;
    outstanding: number;
    payments: number;
    by_method: { method: string; amount: number; payments: number }[];
  };
  colonies: {
    declared: number;
    insured_members: number;
    referrals: number;
    apiaries_recorded: number;
    apiary_colonies: number;
    members_with_apiaries: number;
  };
  mentoring: {
    active_now: number;
    started: number;
    completed: number;
    mentors_engaged: number;
    mentors_willing: number;
  };
  courses: { name: string; year: number; attendees: number; completed: number }[];
  trend: { year: number; members: number; received: number }[];
  /** Today's roll, which is a different question from any year's membership. */
  today: Record<string, number>;
  checks: { total_due_mismatches: number; overpaid_subscriptions: number };
}

export interface RenewalCandidate {
  member_id: number;
  name: string;
  email: string | null;
  category_name: string;
  colony_count: number | null;
  total_due: number;
  needs_bdi_referral: boolean;
  /** They had a subscription last year but never settled it. */
  unpaid_last_year: boolean;
  /** Set only on blocked rows: why they cannot be priced. */
  blocked_reason?: string | null;
}

export interface RenewalPreview {
  year: { id: number; year: number; opens_on: string; due_on: string; is_current: boolean };
  /** null when there is no preceding year to roll anybody forward from. */
  source_year: { id: number; year: number } | null;
  renewable: RenewalCandidate[];
  blocked: RenewalCandidate[];
  already: RenewalCandidate[];
  totals: { renewable: number; blocked: number; already: number; total_due: number };
}

export const PERMISSION_LABELS: Record<string, string> = {
  committee_area: 'Committee area',
  members_view: 'View members',
  members_edit: 'Edit members',
  subscriptions_view: 'View subscriptions',
  payments_record: 'Record payments',
  rates_manage: 'Manage rates',
  email_send: 'Send email',
  email_schedule: 'Schedule email',
  mentoring_manage: 'Manage mentoring',
  permissions_manage: 'Manage permissions',
  audit_view: 'View audit log',
};
